// src/ConsumerApp/api/projects/project.hooks.js

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProjectService } from './project.service';

export const useCreateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['projects', 'createProject'],
    mutationFn: (projectData) => ProjectService.createProject(projectData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['projects', variables?.customer_id],
      });
      queryClient.invalidateQueries({ queryKey: ['deliveryAddresses'] });
    },
  });
};

export const useGetProjectsByCustomer = (customerId) => {
  return useQuery({
    queryKey: ['projects', customerId],
    queryFn: async () => {
      const data = await ProjectService.getProjectsByCustomer(customerId);
      return data?.data || data;
    },
    enabled: typeof customerId === 'string' || typeof customerId === 'number',
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });
};

export const useSelectDeliveryAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['deliveryAddress', 'selectActive'],
    mutationFn: (selectedDeliveryData) =>
      ProjectService.selectDeliveryAddressForCustomer(selectedDeliveryData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['defaultDeliveryAddress', variables?.customer_id],
      });
      queryClient.invalidateQueries({ queryKey: ['deliveryAddresses'] });
      queryClient.invalidateQueries({ queryKey: ['bucketDetails'] });
      queryClient.invalidateQueries({ queryKey: ['bucketSummary', variables?.customer_id] });
    },
  });
};

export const useGetDefaultDeliveryAddress = (customerId) => {
  return useQuery({
    queryKey: ['defaultDeliveryAddress', customerId],
    queryFn: async () => {
      const response = await ProjectService.getDefaultDeliveryAddress(customerId);
      return response?.data || response;
    },
    enabled: !!customerId,
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, payload }) =>
      ProjectService.updateProject({ projectId, payload }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables?.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId) => ProjectService.deleteProject(projectId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.removeQueries({ queryKey: ['project', variables] });
      queryClient.invalidateQueries({ queryKey: ['deliveryAddresses'] });
      queryClient.invalidateQueries({ queryKey: ['defaultDeliveryAddress'] });
    },
  });
};